<script setup lang="ts">
import { IconCheck } from '@/shared/ui/icons';

import type { SelectOption } from './AppSelect.vue';

defineProps<{
  id: string;
  option: SelectOption;
  isActive: boolean;
  isSelected: boolean;
  multiple?: boolean;
  optionClasses: string;
}>();

const emit = defineEmits<{
  activate: [];
  select: [];
}>();

function activate(event: PointerEvent) {
  if (event.pointerType !== 'touch') emit('activate');
}
</script>

<template>
  <div
    :id="id"
    class="select-option flex min-w-0 shrink-0 cursor-pointer items-center gap-x-[5px] rounded-md text-(--base-popup-text-color) select-none"
    :class="[
      optionClasses,
      {
        'select-option-active': isActive,
        'select-option-selected': isSelected && !multiple,
      },
    ]"
    role="option"
    :aria-selected="isSelected"
    @click="emit('select')"
    @pointermove="activate"
  >
    <span
      v-if="multiple"
      aria-hidden="true"
      class="mr-1.5 flex size-5 shrink-0 items-center justify-center rounded-md border"
      :class="
        isSelected
          ? 'border-(--base-accent-color) bg-(--base-accent-color) text-white'
          : 'border-(--base-secondary-color)'
      "
    >
      <IconCheck
        v-if="isSelected"
        :width="12"
        :height="10"
      />
    </span>
    <div class="min-w-0 grow truncate">
      <slot :option="option">
        <!-- Default content provided by parent -->
      </slot>
    </div>
  </div>
</template>

<style scoped>
@reference "../../../tailwind.css";

.select-option-selected {
  @apply bg-(--base-accent-color)/20;
}

.select-option-active:not(.select-option-selected) {
  @apply bg-(--base-popup-hover-color);
}

/* Размеры опций */
.select-option-size-xs {
  @apply min-h-7 px-3 py-1 text-xs;
}

.select-option-size-sm {
  @apply min-h-9 px-3 py-2 text-sm;
}

.select-option-size-md {
  @apply min-h-11 px-3 py-2 text-base;
}

.select-option-size-lg {
  @apply min-h-13 px-4 py-3 text-lg;
}

.select-option-size-xl {
  @apply min-h-15 px-5 py-4 text-xl;
}
</style>
